import { defaultTheme } from '@helpscout/hsds-tokens'
import type { ColorPath, TokenPath } from '@helpscout/hsds-tokens'
import { getToken } from '@helpscout/hsds-tokens/getToken'
import get from 'lodash.get'
import isPlainObject from 'lodash.isplainobject'

export const isHex = (hex: string) =>
  /^#(([A-F0-9]{2}){3,4}|[A-F0-9]{3})$/i.test(hex)
const lightThreshold = 0.61
const optimalTextColorValues = {
  r: 129,
  g: 522,
  b: 49,
}

type RGB = { r: number; g: number; b: number }
type HSL = { h: number; s: number; l: number }

export const hexToRgb = (hex: string) => {
  if (!isHex(hex)) return null
  // Expand shorthand form (e.g. "03F") to full form (e.g. "0033FF")
  const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i
  const expandedHex = hex.replace(
    shorthandRegex,
    (_m: string, r: string, g: string, b: string): string => {
      return r + r + g + g + b + b
    }
  )

  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(expandedHex)

  return result
    ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16),
      }
    : null
}

export const hexToHsl = (hex: string) => {
  if (!isHex(hex)) return null
  const rgb = hexToRgb(hex) as RGB

  return rgbToHsl(rgb.r, rgb.g, rgb.b)
}

export const optimalTextColor = (
  backgroundHex: string,
  propValues = optimalTextColorValues
) => {
  if (!isHex(backgroundHex)) return null
  // Defaults from original formula:
  // r: 299
  // g: 587
  // b: 114
  const defaultPropValues = optimalTextColorValues
  const { r, g, b } = Object.assign({}, defaultPropValues, propValues)
  const backgroundRgb = hexToRgb(backgroundHex) as RGB
  const shade = Math.round(
    (backgroundRgb.r * r + backgroundRgb.g * g + backgroundRgb.b * b) / 1000
  )

  return shade >= 128 ? 'black' : 'white'
}

export const rgbToHsl = (red: number, green: number, blue: number) => {
  if (
    typeof red !== 'number' ||
    typeof green !== 'number' ||
    typeof blue !== 'number'
  )
    return null
  const r = red / 255
  const g = green / 255
  const b = blue / 255

  const max = Math.max(r, g, b)
  const min = Math.min(r, g, b)

  let h = 0
  let s
  const l = (max + min) / 2

  if (max === min) {
    h = 0
    s = 0
  } else {
    const d = max - min
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min)

    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0)
        break
      case g:
        h = (b - r) / d + 2
        break
      case b:
        h = (r - g) / d + 4
        break
    }

    h /= 6
  }

  return {
    h: h % 1 !== 0 ? Math.round(h * 1e2) / 1e2 : h,
    s: s % 1 !== 0 ? Math.round(s * 1e2) / 1e2 : s,
    l: l % 1 !== 0 ? Math.round(l * 1e2) / 1e2 : l,
  }
}

// Source
// https://css-tricks.com/snippets/javascript/lighten-darken-color/
export const lightenDarkenColor = (color: string, value: number) => {
  if (color[0] === '#') {
    color = color.slice(1)
  }

  if (color.length === 3) {
    color = color[0] + color[0] + color[1] + color[1] + color[2] + color[2]
  }

  const num = parseInt(color, 16)

  let r = (num >> 16) + value
  if (r > 255) r = 255
  else if (r < 0) r = 0

  let b = ((num >> 8) & 0x00ff) + value
  if (b > 255) b = 255
  else if (b < 0) b = 0

  let g = (num & 0x0000ff) + value
  if (g > 255) g = 255
  else if (g < 0) g = 0

  return '#' + ('000000' + (g | (b << 8) | (r << 16)).toString(16)).slice(-6)
}

export const lighten = (hex: string, value = 20) => {
  if (!isHex(hex) || typeof value !== 'number') return null
  return lightenDarkenColor(hex, value * 2.55)
}

export const darken = (hex: string, value = 20) => {
  if (!isHex(hex) || typeof value !== 'number') return null
  return lightenDarkenColor(hex, value * 2.55 * -1)
}

export const getColorShade = (
  hex: string,
  propValues = optimalTextColorValues
) => {
  const hsl = hexToHsl(hex) as HSL

  const l = hsl.l
  const isDarkText = optimalTextColor(hex, propValues) === 'black'

  if (l >= 0.9) {
    return 'lightest'
  } else if (l >= lightThreshold) {
    return isDarkText ? 'light' : 'dark'
  } else if (l >= 0.16) {
    return isDarkText ? 'light' : 'dark'
  } else {
    return 'darkest'
  }
}

export const getColor = (
  colorPath?: ColorPath,
  alpha: string | number | null = null
) => {
  if (!colorPath) return 'currentColor'

  let resolvedPath: string = colorPath
  if (!resolvedPath.includes('.')) resolvedPath += '.default'

  const tokenName = `color.${resolvedPath}${alpha ? `.rgb` : ''}`
  // The .rgb suffix is generated at CSS level (see toJs.mjs) but not in
  // the token map, so we assert the type for this internal construction.
  const token = getToken(tokenName as TokenPath)
  if (alpha) {
    return `rgba(${token}, ${alpha})`
  }

  return token
}

/**
 * Retrieves a color/shade from the Color palette
 */
export const getColorHex = (colorCode?: string, theme = 'default') => {
  const colorScheme = theme === 'default' ? defaultTheme.color : {}
  const defaultColor = 'currentColor'

  if (!colorCode) {
    return defaultColor
  }
  // Dot notation
  const splitColor = colorCode.split('.')

  // Default to shade "default"
  if (splitColor.length === 1) {
    splitColor.push('default')
  }

  let index = 0
  let color = colorScheme

  while (color != null && index < splitColor.length) {
    color = color[
      splitColor[index++] as keyof typeof color
    ] as unknown as typeof color
  }

  if (isPlainObject(color)) {
    color = color['default' as keyof typeof color] as unknown as typeof color
  }

  return color || defaultColor
}

/**
 * Retrieves a brand property from ThemeProvider.
 * @param   {Object} props The styled props.
 * @param   {string} path  The props path to retrieve
 * @param   {any} fallback The fallback prop.
 * @returns {any} The fetched property
 */
export const getThemeBrandProp = (props = {}, path = '', fallback = '') => {
  return get(props, `theme.brandColor.${path}`, fallback) as string
}

/**
 * Generates a series of color variables based on a single HEX code used
 * for theming.
 */
export const makeBrandColors = (brandColor?: string | null) => {
  if (brandColor == null) {
    throw new Error('You should give me something to work with')
  }
  // If a css custom property passed, throw error
  if (typeof brandColor === 'string' && brandColor.startsWith('--')) {
    throw new Error(
      'A CSS custom property was passed, please use valid HEX value'
    )
  }
  if (!isHex(brandColor)) {
    throw new Error('Please use a valid HEX value')
  }

  // Let's remove the alpha channel in case is present
  const hexBrandColor = brandColor.slice(0, 7)
  // Possible Values: Darkest, Dark, Light, Lightest
  const colorShade = getColorShade(hexBrandColor)
  const isWhite =
    hexBrandColor.toLowerCase() === '#fff' ||
    hexBrandColor.toLowerCase() === '#ffffff'

  // Setup and adjust props based on theme color choice
  // These colors need to be adjusted based on the hexBrandColor's shade.
  let backgroundColorInteractive
  let backgroundColorHover
  let backgroundColorActive
  let backgroundColorUI
  let backgroundColorUIMuted
  let backgroundColorUIHover
  let backgroundColorUIActive
  let backgroundColorShortcutUI
  let backgroundColorUIFocus
  let svgPathPrimary
  let svgPathSecondary
  let textColor
  let textColorInteractive
  let textColorInactive
  let textColorMuted

  if (colorShade === 'lightest' || colorShade === 'light') {
    backgroundColorHover = darken(hexBrandColor, 3)
    backgroundColorActive = darken(hexBrandColor, 5)
    backgroundColorUI = hexBrandColor
    backgroundColorUIMuted = darken(hexBrandColor, 10)
    backgroundColorUIHover = darken(hexBrandColor, 3)
    backgroundColorUIActive = darken(hexBrandColor, 6)
    backgroundColorShortcutUI = darken(hexBrandColor, 10)
    backgroundColorUIFocus = darken(hexBrandColor, 10)
    svgPathPrimary = darken(hexBrandColor, 30)
    svgPathSecondary = 'white'
  }

  if (colorShade === 'lightest') {
    backgroundColorInteractive = darken(hexBrandColor, 5)
    textColor = darken(hexBrandColor, 70)
    textColorInteractive = darken(hexBrandColor, 70)
    textColorInactive = darken(hexBrandColor, 35)
    textColorMuted = darken(hexBrandColor, 10)

    if (isWhite) {
      textColor = '#394956'
      backgroundColorUIMuted = '#fff'
    }
  }

  if (colorShade === 'light') {
    backgroundColorInteractive = darken(hexBrandColor, 8)
    textColor = darken(hexBrandColor, 55)
    textColorInteractive = darken(hexBrandColor, 55)
    textColorInactive = darken(hexBrandColor, 35)
    textColorMuted = darken(hexBrandColor, 10)
  }

  if (colorShade === 'dark' || colorShade === 'darkest') {
    backgroundColorHover = lighten(hexBrandColor, 3)
    backgroundColorActive = lighten(hexBrandColor, 5)
    backgroundColorUI = hexBrandColor
    textColor = 'white'
    textColorInteractive = 'white'
    textColorInactive = lighten(hexBrandColor, 35)
    textColorMuted = lighten(hexBrandColor, 10)
  }

  if (colorShade === 'dark') {
    backgroundColorInteractive = darken(hexBrandColor, 8)
    backgroundColorUIHover = darken(hexBrandColor, 3)
    backgroundColorUIActive = darken(hexBrandColor, 6)
    backgroundColorShortcutUI = darken(hexBrandColor, 10)
    backgroundColorUIFocus = darken(hexBrandColor, 10)
    backgroundColorUIMuted = darken(hexBrandColor, 6)
    svgPathPrimary = darken(hexBrandColor, 30)
    svgPathSecondary = 'white'
  }

  if (colorShade === 'darkest') {
    backgroundColorInteractive = lighten(hexBrandColor, 16)
    backgroundColorUIHover = lighten(hexBrandColor, 3)
    backgroundColorUIActive = lighten(hexBrandColor, 6)
    backgroundColorShortcutUI = lighten(hexBrandColor, 10)
    backgroundColorUIFocus = lighten(hexBrandColor, 10)
    backgroundColorUIMuted = lighten(hexBrandColor, 10)
    svgPathPrimary = lighten(hexBrandColor, 60)
    svgPathSecondary = lighten(hexBrandColor, 40)
  }

  // Setup props for style generators
  return {
    backgroundColorActive,
    backgroundColorHover,
    backgroundColorInteractive,
    backgroundColorUI,
    backgroundColorUIActive,
    backgroundColorShortcutUI,
    backgroundColorUIFocus,
    backgroundColorUIHover,
    backgroundColorUIMuted,
    brandColor: hexBrandColor,
    colorShade,
    isWhite,
    svgPathPrimary,
    svgPathSecondary,
    textColor,
    textColorInactive,
    textColorInteractive,
    textColorMuted,
  }
}
